import { CoverImageFragment, MediaFragment } from "@src/fragments.generated";
import { ACCEPTED_FILE_TYPES } from "@src/lib/utils/files";
import { FC, ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { Button } from "@src/lib/ui/Button";
import {
  NavList,
  NavListItem,
  NavListPanel,
  NavListTabs,
} from "@src/lib/ui/NavList";
import Text from "@src/lib/ui/Text";
import { useToast } from "@src/lib/hooks/useToast";
import { IGif } from "@giphy/js-types";
import { useTracking } from "@src/features/app/hooks/useTracking";
import {
  useAddMediaDialogCreateMediaMutation,
  useGenerateImageMutation,
} from "@src/features/app/dialogs/addMedia/AddMediaDialog.generated";
import { useModifyImageMutation } from "@src/features/aiAssist/dialogs/AIImageEditDialog.generated";
import { LinkType } from "@src/lib/utils/files";
import { cn } from "@src/lib/utils/cn";
import { Dialog, DialogTrigger, DialogContent } from "@src/lib/ui/Dialog";
import ImagePreviewDialog from "@src/lib/ui/ImagePreviewDialog";
import Resources from "./Resources";
import PasteLink from "./PasteLink";
import StockPhotos from "./StockPhotos";
import StockVideos from "./StockVideos";
import GIFs from "./GIFs";
import { emptyTranslationSet } from "@src/features/knowledgeCheck/utils/prompts";
import {
  PexelsPhoto_StockPhotosFragment,
  UnsplashImage_StockPhotosFragment,
} from "./StockPhotos.generated";
import {
  PexelsVideoQuality,
  Ff,
  BackgroundGradient,
} from "@src/types.generated";
import PhotoOutlineIcon from "@src/lib/icons/18px/photo-outline";
import LinkIcon from "@src/lib/icons/18px/link";
import FolderOpenOutlineIcon from "@src/lib/icons/18px/folder-open-outline";
import VideoOutlineIcon from "@src/lib/icons/18px/video-outline";
import GifIcon from "@src/lib/icons/18px/gif";
import PlusIcon from "@src/lib/icons/18px/plus";
import EmojiOutlineIcon from "@src/lib/icons/18px/emoji-outline";
import Emojis from "@src/features/app/dialogs/addMedia/Emojis";
import { AddMediaResources_TrainingResourceFragment } from "./Resources.generated";
import { useS3Upload, S3Upload } from "@src/features/app/hooks/useS3Upload";
import Spinner from "@src/lib/ui/Spinner";
import { gql } from "@apollo/client";
import PhotoSparkleOutlineIcon from "@src/lib/icons/18px/photo-sparkle-outline";
import SparklesIcon from "@src/lib/icons/18px/sparkles-2-fill";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { DictationTextarea } from "@src/features/aiAssist/DictationButton";
import {
  Tabs as TabsComponent,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@src/lib/ui/Tabs";
import ChevronLeftIcon from "@src/lib/icons/18px/chevron-left";
import ArrowDownloadIcon from "@src/lib/icons/18px/arrow-download";
import EyeOutlineIcon from "@src/lib/icons/18px/eye-outline";
import CircleQuestionOutlineIcon from "@src/lib/icons/18px/circle-question-outline";

type Props = {
  title: string;
  onMediaChange: (media: MediaFragment) => void;
  onResourceChange: (
    trainingResource: AddMediaResources_TrainingResourceFragment,
  ) => void;
  filterResources?: LinkType[];
  /** Omit when the caller opens it through `open`. */
  trigger?: ReactNode;
  /** Controls the dialog from outside (with `onOpenChange`) instead of the trigger. */
  open?: boolean;
  initialCoverImage?: CoverImageFragment;
  onCoverImageChange?: (coverImage: Omit<CoverImageFragment, "id">) => void;
  disableFileTypeSelect?: boolean;
  uploadAccept?: string;
  initialTab?: MainTabs;
  onOpenChange?: (open: boolean) => void;
  allowedGradients?: BackgroundGradient[];
};

export enum MainTabs {
  RESOURCES,
  PASTE_LINK,
  IMAGES,
  VIDEOS,
  GIFS,
  EMOJIS,
  AI_GENERATE,
}

// Export as Tabs for backward compatibility
export const Tabs = MainTabs;

const DEFAULT_TABS = [
  MainTabs.AI_GENERATE,
  MainTabs.GIFS,
  MainTabs.IMAGES,
  MainTabs.VIDEOS,
  MainTabs.PASTE_LINK,
  MainTabs.RESOURCES,
];

const TAB_BUTTON_COPY = {
  [MainTabs.RESOURCES]: "My Resources",
  [MainTabs.PASTE_LINK]: "Paste Link",
  [MainTabs.IMAGES]: "Stock Photos",
  [MainTabs.VIDEOS]: "Stock Videos",
  [MainTabs.GIFS]: "GIFs",
  [MainTabs.EMOJIS]: "Emojis",
  [MainTabs.AI_GENERATE]: "Generate with AI",
};

const TAB_BUTTON_ICON = {
  [MainTabs.RESOURCES]: FolderOpenOutlineIcon,
  [MainTabs.PASTE_LINK]: LinkIcon,
  [MainTabs.IMAGES]: PhotoOutlineIcon,
  [MainTabs.VIDEOS]: VideoOutlineIcon,
  [MainTabs.GIFS]: GifIcon,
  [MainTabs.EMOJIS]: EmojiOutlineIcon,
  [MainTabs.AI_GENERATE]: PhotoSparkleOutlineIcon,
};

const AddMediaDialog: FC<Props> = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  title,
  onMediaChange,
  filterResources,
  initialCoverImage,
  onCoverImageChange,
  onResourceChange,
  trigger,
  disableFileTypeSelect,
  uploadAccept,
  initialTab,
  onOpenChange,
  allowedGradients,
  open: controlledOpen,
}) => {
  const isAiImageGenEnabled = useFeatureFlag(Ff.AiImageGen);
  const tabs = useMemo(() => {
    const t = [];
    if (
      isAiImageGenEnabled &&
      (!filterResources || filterResources.includes(LinkType.IMAGE))
    ) {
      t.push(MainTabs.AI_GENERATE);
    }
    if (
      !filterResources ||
      filterResources.includes(LinkType.IMAGE) ||
      filterResources.includes(LinkType.VIDEO) ||
      filterResources.includes(LinkType.GIF)
    ) {
      t.push(MainTabs.RESOURCES);
    }
    if (!filterResources) {
      t.push(MainTabs.PASTE_LINK);
    }
    if (!filterResources || filterResources.includes(LinkType.IMAGE)) {
      t.push(MainTabs.IMAGES);
    }
    if (!filterResources || filterResources.includes(LinkType.VIDEO)) {
      t.push(MainTabs.VIDEOS);
    }
    if (!filterResources || filterResources.includes(LinkType.GIF)) {
      t.push(MainTabs.GIFS);
    }
    if (filterResources && filterResources.includes(LinkType.EMOJI)) {
      t.push(MainTabs.EMOJIS);
    }
    if (filterResources && filterResources.includes(LinkType.LINK)) {
      t.push(MainTabs.PASTE_LINK);
    }
    return t;
  }, [filterResources, isAiImageGenEnabled]);
  const [selectedTab, setSelectedTab] = useState(
    initialTab ||
      DEFAULT_TABS.find((tab) => tabs.includes(tab)) ||
      MainTabs.RESOURCES,
  );
  const { addErrorToast } = useToast();
  const [uncontrolledOpen, setOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const closeDialog = useCallback(() => {
    setOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);
  const onMediaChangeAndClose = useCallback(
    (media: MediaFragment) => {
      closeDialog();
      onMediaChange(media);
    },
    [closeDialog, onMediaChange],
  );
  const [createMedia] = useAddMediaDialogCreateMediaMutation({
    onCompleted(data) {
      if (data.createMedia.success && data.createMedia.media) {
        onMediaChangeAndClose(data.createMedia.media);
      } else {
        addErrorToast({
          ...data.createMedia.error,
          callsite: "add_media_dialog",
        });
      }
    },
    onError: (error) => {
      addErrorToast({
        ...error,
        callsite: "add_media_dialog",
      });
    },
  });
  const onAddLink = useCallback(
    (link: string) => {
      createMedia({
        variables: {
          input: {
            mediaUrls: emptyTranslationSet({
              en: link,
              translationsDisabled: true,
            }),
          },
        },
      });
    },
    [createMedia],
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const onUploadComplete = useCallback(
    (upload: S3Upload) => {
      createMedia({
        variables: {
          input: {
            mediaUrls: emptyTranslationSet({
              en: upload.url,
              translationsDisabled: true,
            }),
          },
        },
      });
    },
    [createMedia],
  );

  const onUploadProgress = useCallback((fileId: string, progress: number) => {
    setUploadProgress(progress);
  }, []);

  const { handleFileUpload, isUploading } = useS3Upload({
    onUploadComplete,
    onUploadProgress,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        setOpen(val);
        onOpenChange?.(val);
      }}
    >
      {trigger && (
        <DialogTrigger onClick={(e) => e.stopPropagation()} asChild>
          {trigger}
        </DialogTrigger>
      )}
      <DialogContent
        className="z-[1000] w-[800px] max-w-[800px] p-4 shadow-xl"
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => e.stopPropagation()}
        onDragLeave={(e) => e.stopPropagation()}
        onDrop={(e) => e.preventDefault()}
        onPointerDownCapture={(e) => e.stopPropagation()}
        onMouseDownCapture={(e) => e.stopPropagation()}
        onTouchStartCapture={(e) => e.stopPropagation()}
        onWheelCapture={(e) => e.stopPropagation()}
      >
        <NavListTabs
          asChild
          // MainTabs is a numeric enum; tab values are strings.
          value={String(selectedTab)}
          onValueChange={(value) => setSelectedTab(Number(value) as MainTabs)}
          // Most sources fetch on mount: arrows only move focus, so passing
          // over a source doesn't load it.
          activationMode="manual"
        >
          <div className="flex h-[85vh] w-full items-start gap-10">
            <div className="flex h-full w-[212px] shrink-0 flex-col items-start self-stretch overflow-y-auto">
              <NavList aria-label="Media sources">
                {tabs.map((tab) => {
                  const Icon = TAB_BUTTON_ICON[tab];
                  return (
                    <NavListItem
                      key={`add-media-tab-${tab}`}
                      value={String(tab)}
                      icon={<Icon />}
                    >
                      {TAB_BUTTON_COPY[tab]}
                    </NavListItem>
                  );
                })}
              </NavList>
              <input
                ref={fileInputRef}
                type="file"
                id="fileInput"
                className="hidden"
                multiple={false}
                accept={uploadAccept || ACCEPTED_FILE_TYPES.toString()}
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleFileUpload(e.target.files[0], "add-media");
                  }
                }}
              />
              <div className="flex flex-1 flex-col items-end justify-end gap-2 self-stretch">
                <Button
                  className="self-stretch"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  variant={isUploading ? "secondary" : "default"}
                >
                  {isUploading ? (
                    <>
                      <Spinner size={18} />
                      <span className="ml-2">
                        Uploading... {uploadProgress}%
                      </span>
                    </>
                  ) : (
                    <>
                      <PlusIcon />
                      <span className="ml-2">Upload</span>
                    </>
                  )}
                </Button>

                <Button
                  variant="outline"
                  className="self-stretch"
                  onClick={closeDialog}
                  disabled={isUploading}
                >
                  Done
                </Button>
              </div>
            </div>
            <NavListPanel
              value={String(selectedTab)}
              className="flex h-full flex-1 flex-col items-start gap-1.5 overflow-hidden"
            >
              {selectedTab !== MainTabs.AI_GENERATE && (
                <Text variant="body-title" className="mb-2">
                  {TAB_BUTTON_COPY[selectedTab]}
                </Text>
              )}
              {selectedTab === MainTabs.RESOURCES && (
                <Resources
                  onResourceClick={(trainingResource) => {
                    closeDialog();
                    onResourceChange?.(trainingResource);
                  }}
                  filterResources={filterResources}
                  disableFileTypeSelect={disableFileTypeSelect}
                />
              )}
              {selectedTab === MainTabs.PASTE_LINK && (
                <PasteLink onAddLink={onAddLink} />
              )}
              {selectedTab === MainTabs.IMAGES && (
                <StockPhotos
                  onPexelsPhotoSelect={(
                    photo: PexelsPhoto_StockPhotosFragment,
                  ) => {
                    createMedia({
                      variables: {
                        input: {
                          mediaUrls: emptyTranslationSet({
                            en: photo.src.original,
                            translationsDisabled: true,
                          }),
                        },
                      },
                    });
                  }}
                  onUnsplashImageSelect={(
                    image: UnsplashImage_StockPhotosFragment,
                  ) => {
                    createMedia({
                      variables: {
                        input: {
                          mediaUrls: emptyTranslationSet({
                            en: image.urls.full,
                            translationsDisabled: true,
                          }),
                        },
                      },
                    });
                  }}
                />
              )}
              {selectedTab === MainTabs.VIDEOS && (
                <StockVideos
                  onPexelsVideoSelect={(video) => {
                    let videoFile = video.videoFiles.find(
                      (f) => f.quality === PexelsVideoQuality.Hd,
                    );
                    if (!videoFile) {
                      videoFile = video.videoFiles[0];
                    }
                    createMedia({
                      variables: {
                        input: {
                          mediaUrls: emptyTranslationSet({
                            en: videoFile.link,
                            translationsDisabled: true,
                          }),
                        },
                      },
                    });
                  }}
                />
              )}
              {selectedTab === MainTabs.GIFS && (
                <GIFs
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  onGIFSelect={(gif: IGif) => {
                    createMedia({
                      variables: {
                        input: {
                          mediaUrls: emptyTranslationSet({
                            en: gif.images.downsized_large.url.split("?")[0],
                            translationsDisabled: true,
                          }),
                        },
                      },
                    });
                  }}
                />
              )}
              {selectedTab === MainTabs.EMOJIS && (
                <Emojis
                  coverImage={initialCoverImage}
                  onCoverImageSelected={onCoverImageChange}
                  allowedGradients={allowedGradients}
                />
              )}
              {selectedTab === MainTabs.AI_GENERATE && (
                <AIImageGenerateContent
                  onImageGenerated={(imageUrl) => {
                    createMedia({
                      variables: {
                        input: {
                          mediaUrls: emptyTranslationSet({
                            en: imageUrl,
                            translationsDisabled: true,
                          }),
                        },
                      },
                    });
                  }}
                />
              )}
            </NavListPanel>
          </div>
        </NavListTabs>
      </DialogContent>
    </Dialog>
  );
};

enum AITabs {
  GENERATE = "generate",
  UPLOAD = "upload",
}

const AIImageGenerateContent: FC<{
  onImageGenerated: (imageUrl: string) => void;
}> = ({ onImageGenerated }) => {
  const [selectedAITab, setSelectedAITab] = useState<AITabs>(AITabs.GENERATE);
  const [prompt, setPrompt] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedImageUrls, setGeneratedImageUrls] = useState<string[]>([]);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [isIterating, setIsIterating] = useState(false);
  const [editPrompt, setEditPrompt] = useState("");
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addErrorToast, addToast } = useToast();
  const [generateImage] = useGenerateImageMutation();
  const [modifyImage] = useModifyImageMutation();
  const { trackEvent } = useTracking();

  const { handleFileUpload, isUploading } = useS3Upload({
    onUploadComplete: (upload: S3Upload) => {
      setUploadedImageUrl(upload.url);
      setGeneratedImageUrls([upload.url]);
      setSelectedImageUrl(upload.url);
      setIsIterating(true);
      addToast({
        message: "Image uploaded! Now describe how you'd like to edit it.",
      });
    },
    onUploadProgress: () => {},
  });
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      addErrorToast({
        message:
          "Please enter a prompt describing the image you want to generate",
        callsite: "ai_image_generate",
      });
      return;
    }
    setIsProcessing(true);
    setGeneratedImageUrls([]);

    const startTime = Date.now();

    try {
      const NUM_VARIATIONS = 4;
      const results = await Promise.all(
        Array.from({ length: NUM_VARIATIONS }).map(() =>
          generateImage({
            variables: {
              input: {
                prompt: prompt.trim(),
              },
            },
          }),
        ),
      );

      const urls = results
        .map((r) => r.data?.generateImage?.imageUrl)
        .filter((u): u is string => Boolean(u));

      if (urls.length > 0) {
        const duration = Date.now() - startTime;

        setGeneratedImageUrls(urls);
        setSelectedImageUrl(urls[0] || null);
        setIsIterating(true);
        addToast({
          message: `Generated ${urls.length} image${urls.length > 1 ? "s" : ""}!`,
        });

        // Track successful AI image generation
        trackEvent("ai_image_generated", {
          prompt: prompt.trim(),
          image_urls: urls,
          duration_ms: duration,
          location: "add_media_dialog",
        });

        if (urls.length < NUM_VARIATIONS) {
          addErrorToast({
            message: `Only ${urls.length} of ${NUM_VARIATIONS} images were generated. The AI service may be temporarily busy — try regenerating.`,
            callsite: "ai_image_generate_partial",
          });
        }
      } else {
        const isServiceUnavailable = results.some(
          (r) =>
            r.data?.generateImage?.error?.code === "AI_SERVICE_UNAVAILABLE",
        );
        addErrorToast({
          message: isServiceUnavailable
            ? "The AI image service is temporarily unavailable. Please try again."
            : "Failed to generate image with AI. Please try again.",
          callsite: "ai_image_generate",
        });
      }
    } catch {
      addErrorToast({
        message: "Something went wrong. Please try again.",
        callsite: "ai_image_generate",
      });
    } finally {
      setIsProcessing(false);
    }
  }, [prompt, addToast, addErrorToast, generateImage, trackEvent]);

  const handleUseImage = useCallback(() => {
    if (selectedImageUrl) {
      onImageGenerated(selectedImageUrl);
      setPrompt("");
      setGeneratedImageUrls([]);
      setSelectedImageUrl(null);
      setIsIterating(false);
      setEditPrompt("");
    }
  }, [selectedImageUrl, onImageGenerated]);

  const handleStartOver = useCallback(() => {
    setGeneratedImageUrls([]);
    setSelectedImageUrl(null);
    setPrompt("");
    setIsIterating(false);
    setEditPrompt("");
    setUploadedImageUrl(null);
  }, []);

  const handleModify = useCallback(async () => {
    if (!selectedImageUrl || !editPrompt.trim()) {
      addErrorToast({
        message: "Please enter a prompt describing how to edit the image",
        callsite: "ai_image_iterate",
      });
      return;
    }

    setIsProcessing(true);
    const startTime = Date.now();

    try {
      const result = await modifyImage({
        variables: {
          input: {
            imageUrl: selectedImageUrl,
            prompt: editPrompt.trim(),
          },
        },
      });

      if (
        result.data?.modifyImage?.success &&
        result.data?.modifyImage?.imageUrl
      ) {
        const newUrl = result.data?.modifyImage?.imageUrl;
        const duration = Date.now() - startTime;

        setSelectedImageUrl(newUrl);
        setGeneratedImageUrls((prev) =>
          prev.length === 0
            ? [newUrl]
            : prev.map((u) => (u === selectedImageUrl ? newUrl : u)),
        );
        setEditPrompt("");
        addToast({
          message: "Image modified successfully!",
        });

        // Track successful AI image modification
        trackEvent("ai_image_modified", {
          prompt: editPrompt.trim(),
          original_url: selectedImageUrl,
          modified_url: newUrl,
          duration_ms: duration,
          location: "add_media_dialog",
        });
      } else {
        addErrorToast({
          message:
            result.data?.modifyImage?.error?.code === "AI_SERVICE_UNAVAILABLE"
              ? "The AI image service is temporarily unavailable. Please try again."
              : "Failed to modify image with AI. Please try again.",
          callsite: "ai_image_iterate",
        });
      }
    } catch {
      addErrorToast({
        message: "Something went wrong. Please try again.",
        callsite: "ai_image_iterate",
      });
    } finally {
      setIsProcessing(false);
    }
  }, [
    selectedImageUrl,
    editPrompt,
    addToast,
    addErrorToast,
    modifyImage,
    trackEvent,
  ]);

  const downloadImage = useCallback(
    async (url: string) => {
      try {
        setIsProcessing(true);
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = "generated-image.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(blobUrl);
      } catch {
        addErrorToast({
          message: "Failed to download image",
          callsite: "ai_image_download",
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [addErrorToast],
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file.type.startsWith("image/")) {
          handleFileUpload(file, "ai-reference-image");
          setSelectedAITab(AITabs.UPLOAD);
        } else {
          addErrorToast({
            message: "Please upload an image file",
            callsite: "ai_image_upload",
          });
        }
      }
    },
    [handleFileUpload, addErrorToast],
  );

  return (
    <div className="flex size-full flex-col gap-4 overflow-y-auto px-0.5">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) {
            handleFileUpload(e.target.files[0], "ai-reference-image");
            setSelectedAITab(AITabs.UPLOAD);
          }
        }}
      />

      <div className="flex w-full items-center justify-between gap-2">
        <Text variant="body-title">
          {TAB_BUTTON_COPY[MainTabs.AI_GENERATE]}
        </Text>
        {generatedImageUrls.length > 0 && (
          <Button
            variant="ghost"
            onClick={handleStartOver}
            disabled={isProcessing}
            className="w-fit gap-1 px-2"
          >
            <ChevronLeftIcon />
            Back
          </Button>
        )}
      </div>

      {generatedImageUrls.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            {prompt && (
              <Text
                variant="fine-print-description"
                multiline
                className="w-full break-words whitespace-normal text-muted-foreground"
              >
                {prompt}
              </Text>
            )}
            {uploadedImageUrl && !prompt && (
              <Text
                variant="fine-print-description"
                className="text-muted-foreground"
              >
                Uploaded image
              </Text>
            )}
            <div className="grid w-full max-w-[440px] grid-cols-2 gap-3 self-center">
              {generatedImageUrls.map((url) => {
                const isSelected = url === selectedImageUrl;
                return (
                  <button
                    key={url}
                    type="button"
                    onClick={() => setSelectedImageUrl(url)}
                    className={`group relative overflow-hidden rounded-2xl border border-gray-6`}
                    style={{ aspectRatio: "1 / 1" }}
                  >
                    <img
                      src={url}
                      alt="Generated option"
                      className="absolute inset-0 size-full object-cover"
                    />
                    {isSelected ? (
                      <div className="pointer-events-none absolute inset-0 rounded-2xl border-4 border-primary" />
                    ) : null}
                    <div className="absolute inset-0 hidden items-center justify-center gap-2 group-hover:flex">
                      <Button
                        size="icon-md"
                        variant="outline"
                        className="shadow-large"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImageUrl(url);
                          setShowPreviewDialog(true);
                        }}
                        disabled={isProcessing}
                      >
                        <EyeOutlineIcon className="size-4" />
                      </Button>
                      <Button
                        size="icon-md"
                        variant="outline"
                        className="shadow-large"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadImage(url);
                        }}
                        disabled={isProcessing}
                      >
                        <ArrowDownloadIcon className="size-4" />
                      </Button>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-center gap-2">
              {!uploadedImageUrl && (
                <Button
                  variant="outline"
                  onClick={handleGenerate}
                  disabled={isProcessing}
                >
                  Regenerate
                </Button>
              )}
              <Button
                onClick={handleUseImage}
                disabled={isProcessing || !selectedImageUrl}
              >
                Use Image
              </Button>
            </div>
          </div>
        </div>
      )}

      {generatedImageUrls.length === 0 && (
        <TabsComponent
          size="sm"
          value={selectedAITab}
          onValueChange={(value: string) => setSelectedAITab(value as AITabs)}
          className="w-full"
        >
          <div className="flex border-b border-border-primary">
            <TabsList fill aria-label="AI image">
              <TabsTrigger value={AITabs.GENERATE}>
                Generate New Image
              </TabsTrigger>
              <TabsTrigger value={AITabs.UPLOAD}>Upload and Edit</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent
            value={AITabs.GENERATE}
            className="mt-4 flex flex-col gap-4"
          >
            <div className="flex flex-col overflow-hidden rounded-xl border border-gray-2 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-gray-3 px-3 py-2">
                <Text variant="compact-title">
                  Describe what you want to create
                </Text>
                <a
                  href="https://help.opus.so/en/articles/12624830-best-practices-for-ai-image-generation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-gray-6 hover:text-gray-11"
                >
                  <CircleQuestionOutlineIcon className="size-3.5 text-gray-6" />
                  <span className="text-xs text-gray-6">Prompt help</span>
                </a>
              </div>
              <DictationTextarea
                placeholder="E.g., a cozy restaurant interior at sunset, a plate of pancakes on a wooden table"
                value={prompt}
                onValueChange={setPrompt}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (!isProcessing && prompt.trim()) {
                      handleGenerate();
                    }
                  }
                }}
                rows={4}
                className="resize-none border-none p-3 focus-visible:ring-0 focus-visible:ring-offset-0"
                disabled={isProcessing}
              />
              <div className="flex items-center justify-end p-2">
                <Button
                  onClick={handleGenerate}
                  disabled={isProcessing || !prompt.trim()}
                  size="sm"
                  className="gap-1.5 bg-ai px-3 text-sm text-white hover:bg-purple-9"
                >
                  {isProcessing ? (
                    <>
                      <Spinner size={16} />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="size-4" />
                      <span>Generate</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent
            value={AITabs.UPLOAD}
            className="mt-4 flex flex-col gap-4"
          >
            <div
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={cn(
                "flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed p-12 transition-colors",
                isDragging
                  ? "bg-primary/5 border-primary"
                  : "border-gray-6 bg-gray-1",
              )}
            >
              <div className="flex flex-col items-center gap-2">
                <PlusIcon className="size-8 text-gray-10" />
                <Text variant="compact-title" className="text-center">
                  {isDragging ? "Drop image here" : "Drag & drop an image"}
                </Text>
                <Text
                  variant="fine-print-description"
                  className="text-center text-muted-foreground"
                >
                  or click the button below to browse
                </Text>
              </div>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isProcessing}
                size="lg"
              >
                {isUploading ? (
                  <>
                    <Spinner size={18} />
                    Uploading...
                  </>
                ) : (
                  <>
                    <PlusIcon />
                    Choose Image
                  </>
                )}
              </Button>
            </div>
          </TabsContent>
        </TabsComponent>
      )}

      {isIterating && (
        <div className="flex flex-col gap-2">
          <Text variant="compact-title">
            Or describe how you'd like to edit this image…
          </Text>
          <div className="flex gap-2">
            <DictationTextarea
              placeholder="E.g., 'Add a blue sky background' or 'Make the colors more vibrant'"
              value={editPrompt}
              onValueChange={setEditPrompt}
              rows={4}
              containerClassName="flex-1"
              className="resize-none"
              disabled={isProcessing}
            />
            <Button
              size="sm"
              onClick={handleModify}
              disabled={isProcessing || !editPrompt.trim()}
              className="self-start"
            >
              {isProcessing ? (
                <>
                  <Spinner size={18} />
                  Modifying...
                </>
              ) : (
                "Modify Image"
              )}
            </Button>
          </div>
        </div>
      )}

      <ImagePreviewDialog
        open={showPreviewDialog}
        onOpenChange={setShowPreviewDialog}
        imageUrl={selectedImageUrl}
      />
    </div>
  );
};

export default AddMediaDialog;

gql`
  query AddMediaDialogTrainingResources($search: String!) {
    TrainingResources: AdminTrainingResources(search: $search) {
      id
      publishedVersion {
        ...TrainingResourceVersion
      }
    }
  }
  mutation AddMediaDialogCreateMedia($input: MediaInput!) {
    createMedia(input: $input) {
      success
      error {
        code
      }
      media {
        ...Media
      }
    }
  }

  mutation GenerateImage($input: GenerateImageInput!) {
    generateImage(input: $input) {
      success
      imageUrl
      error {
        code
      }
    }
  }
`;
